import { Button, Modal, TextField, Tooltip } from '@lego/connect-components-react';
import type { IconName } from '@lego/icons';
import type { MouseEvent } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Table } from '../../components';
import type { TableColumn } from '../../components/table';
import { useGraphUser } from '../../hooks/graph/use-graph-user';
import type { LabelData } from '../../types/types';
import type { FormListItem } from './hooks';

type FormListProps = {
  forms: FormListItem[];
  labels: { items: LabelData[] } | undefined;
  isDeleting: boolean;
  onCreateClick: () => void;
  onEdit: (form: FormListItem) => void;
  onShare: (form: FormListItem) => void;
  onDelete: (form: FormListItem) => void;
};

const LastUpdatedByDisplay = ({ oid }: { oid: string }) => {
  const { data } = useGraphUser(oid);
  return <span>{data?.displayName ?? oid}</span>;
};

const TooltipIconButton = ({
  iconName,
  label,
  variant = 'primary',
  disabled,
  onClick,
}: {
  iconName: IconName;
  label: string;
  variant?: 'primary' | 'negative';
  disabled?: boolean;
  onClick: (e: MouseEvent) => void;
}) => (
  <Tooltip position="top">
    <Tooltip.Trigger>
      <Button
        isIconOnly
        iconName={iconName}
        aria-label={label}
        size="small"
        variant={variant}
        appearance="ghost"
        onClick={onClick}
        disabled={disabled}
      />
    </Tooltip.Trigger>
    <Tooltip.Content>{label}</Tooltip.Content>
  </Tooltip>
);

const RowActions = ({
  item,
  isDeleting,
  onEdit,
  onShare,
  onDelete,
}: {
  item: FormListItem;
  isDeleting: boolean;
  onEdit: (form: FormListItem) => void;
  onShare: (form: FormListItem) => void;
  onDelete: (form: FormListItem) => void;
}) => {
  const { t } = useTranslation();

  const stopAndCall = (fn: (form: FormListItem) => void) => (e: MouseEvent) => {
    e.stopPropagation();
    fn(item);
  };

  return (
    <div className="flex justify-end gap-100">
      <TooltipIconButton
        iconName="pen"
        label={t('formPage.action.edit')}
        onClick={stopAndCall(onEdit)}
        disabled={isDeleting}
      />
      <TooltipIconButton
        iconName="share-nodes"
        label={t('formPage.action.share')}
        onClick={stopAndCall(onShare)}
        disabled={isDeleting}
      />
      <TooltipIconButton
        iconName="trash"
        label={t('formPage.action.deleteForm')}
        variant="negative"
        onClick={stopAndCall(onDelete)}
        disabled={isDeleting}
      />
    </div>
  );
};

const FormList = ({
  forms,
  labels,
  isDeleting,
  onCreateClick,
  onEdit,
  onShare,
  onDelete,
}: FormListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deleteConfirm, setDeleteConfirm] = useState<FormListItem | null>(null);
  const [shareForm, setShareForm] = useState<FormListItem | null>(null);
  const deleteRef = useRef<HTMLElement>(null);
  const shareRef = useRef<HTMLElement>(null);

  const getLabelName = useCallback(
    (lid: number) => labels?.items.find((l: LabelData) => l.id === lid)?.name ?? `ID ${lid}`,
    [labels]
  );

  const handleDeleteRequest = useCallback((form: FormListItem) => {
    setDeleteConfirm(form);
  }, []);

  const handleDeleteConfirm = () => {
    if (deleteConfirm) {
      onDelete(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  const handleShareRequest = useCallback((form: FormListItem) => {
    setShareForm(form);
  }, []);

  const shareUrl = shareForm
    ? `${window.location.origin}/form-print?formId=${shareForm.publicId}`
    : '';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    onShare(shareForm!);
    setShareForm(null);
  };

  const columns: TableColumn<FormListItem>[] = useMemo(
    () => [
      {
        key: 'name',
        label: t('formPage.display.name'),
        sortable: true,
      },
      {
        key: 'labelId',
        label: t('formPage.display.label'),
        sortable: true,
        accessor: (form: FormListItem) => getLabelName(form.labelId),
      },
      {
        key: 'labelRevisions',
        label: t('formPage.display.revisions'),
        sortable: false,
        accessor: (form: FormListItem) =>
          form.labelRevisions?.length ? form.labelRevisions.join(', ') : '-',
      },
      {
        key: 'printerIds',
        label: t('formPage.display.printers'),
        sortable: false,
        accessor: (form: FormListItem) =>
          form.printerIds?.length ? `${form.printerIds.length}` : '-',
      },
      {
        key: 'groups',
        label: t('formPage.display.groups'),
        sortable: false,
        accessor: (form: FormListItem) => (form.groups?.length ? form.groups.join(', ') : '-'),
      },
      {
        key: 'updatedAt',
        label: t('formPage.display.updated'),
        sortable: true,
        accessor: (form: FormListItem) =>
          form.updatedAt ? new Date(form.updatedAt).toLocaleDateString() : '-',
      },
      {
        key: 'updatedBy',
        label: t('formPage.display.updatedBy'),
        sortable: true,
        accessor: (form: FormListItem) => form.updatedBy ?? form.createdBy,
        render: (_value, item) => <LastUpdatedByDisplay oid={item.updatedBy ?? item.createdBy} />,
      },
      {
        key: 'actions',
        label: '',
        sortable: false,
        render: (_value, item) => (
          <RowActions
            item={item as unknown as FormListItem}
            isDeleting={isDeleting}
            onEdit={onEdit}
            onShare={handleShareRequest}
            onDelete={handleDeleteRequest}
          />
        ),
      },
    ],
    [t, isDeleting, getLabelName, onEdit, handleShareRequest, handleDeleteRequest]
  );

  return (
    <>
      <div className="px-400 py-400">
        <div className="flex items-center justify-between mb-300">
          <h1 className="text-heading-md font-brand font-bold">
            {t('formPage.display.formsTitle')} ({forms.length})
          </h1>
          <Button
            variant="primary"
            size="small"
            onClick={onCreateClick}
            iconName="control-plus"
            iconPosition="start"
          >
            {t('formPage.action.createForm')}
          </Button>
        </div>

        <Table
          data={forms as unknown as Record<string, unknown>[]}
          columns={columns as TableColumn<Record<string, unknown>>[]}
          onRowClick={(item) => {
            const form = item as unknown as FormListItem;
            navigate(`/form-print?formId=${form.id}`);
          }}
          loading={false}
          emptyMessage={t('formPage.display.noFormsFound')}
          searchPlaceholder={t('formPage.display.searchFormsPlaceholder')}
          ariaLabel={t('formPage.display.formsTitle')}
        />
      </div>

      {/* Delete confirmation */}
      <Modal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        triggerRef={deleteRef}
        size="small"
      >
        <Modal.Title>{t('formPage.action.deleteForm')}</Modal.Title>
        <Modal.Content>
          <p>{t('formPage.response.confirmDelete', { name: deleteConfirm?.name || '' })}</p>
        </Modal.Content>
        <Modal.Footer>
          <Button
            variant="neutral"
            appearance="ghost"
            onClick={() => setDeleteConfirm(null)}
            disabled={isDeleting}
          >
            {t('formPage.action.cancel')}
          </Button>
          <Button
            variant="negative"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
            loading={isDeleting}
            loadingText={t('formPage.action.deleting')}
          >
            {t('formPage.action.deleteForm')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Share modal */}
      <Modal
        isOpen={shareForm !== null}
        onClose={() => setShareForm(null)}
        triggerRef={shareRef}
        size="medium"
      >
        <Modal.Title>{t('formPage.action.share')}</Modal.Title>
        <Modal.Content>
          <TextField
            size="medium"
            readOnly
            value={shareUrl}
            aria-label={t('formPage.action.share')}
          />
        </Modal.Content>
        <Modal.Footer>
          <Button variant="neutral" appearance="ghost" onClick={() => setShareForm(null)}>
            {t('formPage.action.cancel')}
          </Button>
          <Button variant="primary" onClick={handleCopyLink}>
            {t('formPage.action.copyLink')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FormList;
